import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  ArrowLeft, ArrowRight, CheckCircle, CreditCard, Building2, 
  User, MapPin, Phone, Mail, Package, PoundSterling, FileText,
  Shield, Calendar, Star, Crown, Clock, Plus, Info, Users, Percent
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminRetailerOnboarding = ({ onClose, user }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [formData, setFormData] = useState({
    // Business Information
    business_name: '',
    email_address: '',
    address: '',
    postcode: '',
    main_contact: '',
    mobile_phone: '',
    
    // Business Details
    services_offered: '',
    products_offered: '',
    target_audience: '',
    special_instructions: '',
    discount_details: '',
    
    // Product/Service List (up to 10 items)
    discount_applicable_items: [''],
    
    // Minimum expenditure to activate discount
    minimum_expenditure: '',
    
    // Dynamic Discount System
    discount_type: 'flat', // 'flat' or 'percentage'
    flat_discount_amount: '',
    category_discounts: {
      students: '',
      pensioners: '',
      government_workers: '',
      nhs_key_workers: '',
      general_public: ''
    },
    
    // Admin-specific fields
    selected_package: 'standard',
    admin_status: 'active', // 'active' or 'pending'
    mark_as_paid: true,
    payment_notes: '',
    setup_fee: 35,
    monthly_fee: 15,
    
    // Generated fields
    generated_url: ''
  });

  const packageDetails = {
    standard: { name: 'Standard', monthly: 15, setup: 35, color: 'bg-blue-500' },
    deluxe: { name: 'Deluxe', monthly: 25, setup: 35, color: 'bg-orange' },
    platinum: { name: 'Platinum', monthly: 35, setup: 35, color: 'bg-amber' },
    elite: { name: 'Elite/Sponsor', monthly: 500, setup: 100, color: 'bg-purple-600' }
  };

  const steps = [
    { id: 1, title: 'Package & Status', icon: <Package className="h-5 w-5" /> },
    { id: 2, title: 'Business Information', icon: <Building2 className="h-5 w-5" /> },
    { id: 3, title: 'Business Details', icon: <FileText className="h-5 w-5" /> },
    { id: 4, title: 'Payment & Review', icon: <PoundSterling className="h-5 w-5" /> },
    { id: 5, title: 'Confirmation', icon: <CheckCircle className="h-5 w-5" /> }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-update fees based on package selection
    if (field === 'selected_package' && packageDetails[value]) {
      const pkg = packageDetails[value];
      setFormData(prev => ({
        ...prev,
        setup_fee: pkg.setup,
        monthly_fee: pkg.monthly
      }));
    }
  };

  // Generate chatbot URL automatically
  React.useEffect(() => {
    if (formData.business_name) {
      const urlFriendlyName = formData.business_name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      setFormData(prev => ({
        ...prev,
        generated_url: `https://${urlFriendlyName}.shoppersdiscount.club`
      }));
    }
  }, [formData.business_name]);

  // Discount system helper functions
  const addDiscountItem = () => {
    if (formData.discount_applicable_items.length < 10) {
      setFormData(prev => ({
        ...prev,
        discount_applicable_items: [...prev.discount_applicable_items, '']
      }));
    }
  };

  const removeDiscountItem = (index) => {
    if (formData.discount_applicable_items.length > 1) {
      setFormData(prev => ({
        ...prev,
        discount_applicable_items: prev.discount_applicable_items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateDiscountItem = (index, value) => {
    setFormData(prev => ({
      ...prev,
      discount_applicable_items: prev.discount_applicable_items.map((item, i) => 
        i === index ? value : item
      )
    }));
  };

  const updateCategoryDiscount = (category, value) => {
    const numValue = parseFloat(value);
    if (value !== '' && (isNaN(numValue) || numValue < 0 || numValue > 50)) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      category_discounts: {
        ...prev.category_discounts,
        [category]: value
      }
    }));
  };

  const updateFlatDiscount = (value) => {
    const numValue = parseFloat(value);
    if (value !== '' && (isNaN(numValue) || numValue < 0 || numValue > 100)) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      flat_discount_amount: value
    }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return formData.selected_package && formData.admin_status;
      case 2:
        return formData.business_name && formData.email_address && formData.address && 
               formData.postcode && formData.main_contact && formData.mobile_phone;
      case 3:
        return formData.services_offered && formData.products_offered;
      case 4:
        return true; // Admin can proceed without payment validation
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      if (currentStep === 4) {
        submitAdminRetailer();
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const submitAdminRetailer = async () => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Prepare data for admin retailer creation
      const retailerData = {
        ...formData,
        created_by_admin: true,
        payment_source: 'Manual',
        application_status: formData.admin_status,
        payment_status: formData.mark_as_paid ? 'completed' : 'pending',
        admin_notes: `Created by admin: ${user?.email}. Payment notes: ${formData.payment_notes}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const response = await axios.post(`${API}/admin/retailer-onboarding`, retailerData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setConfirmation({
        applicationId: response.data.id,
        businessName: formData.business_name,
        package: packageDetails[formData.selected_package],
        chatbotUrl: formData.generated_url,
        status: formData.admin_status
      });
      
      setCurrentStep(5);
    } catch (error) {
      console.error('Error creating admin retailer:', error);
      alert('Error creating retailer. Please check the details and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-charcoal mb-2">Package & Status Assignment</h3>
              <p className="text-gray-600">Choose the package and initial status for this retailer</p>
            </div>

            <div>
              <Label className="text-base font-medium text-charcoal mb-3 block">Select Package Type</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(packageDetails).map(([key, pkg]) => (
                  <Card 
                    key={key}
                    className={`cursor-pointer transition-all duration-200 ${
                      formData.selected_package === key 
                        ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleInputChange('selected_package', key)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`${pkg.color} p-2 rounded-lg text-white`}>
                            <Package className="h-4 w-4" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-charcoal">{pkg.name}</h4>
                            <p className="text-sm text-gray-600">£{pkg.monthly}/month</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Setup: £{pkg.setup}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-base font-medium text-charcoal mb-3 block">Initial Status</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Card 
                  className={`cursor-pointer transition-all duration-200 ${
                    formData.admin_status === 'active' 
                      ? 'border-green-500 ring-2 ring-green-500/20 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleInputChange('admin_status', 'active')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <h4 className="font-semibold text-charcoal">Active</h4>
                        <p className="text-sm text-gray-600">Goes live immediately</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card 
                  className={`cursor-pointer transition-all duration-200 ${
                    formData.admin_status === 'pending' 
                      ? 'border-orange-500 ring-2 ring-orange-500/20 bg-orange-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleInputChange('admin_status', 'pending')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-orange-600" />
                      <div>
                        <h4 className="font-semibold text-charcoal">Pending Review</h4>
                        <p className="text-sm text-gray-600">Hold for approval</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-charcoal mb-2">Business Information</h3>
              <p className="text-gray-600">Enter the retailer's business details</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Business Name *</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  placeholder="Business name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={formData.email_address}
                  onChange={(e) => handleInputChange('email_address', e.target.value)}
                  placeholder="business@example.com"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label>Business Address *</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Full business address"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Postcode *</Label>
                <Input
                  value={formData.postcode}
                  onChange={(e) => handleInputChange('postcode', e.target.value)}
                  placeholder="Postcode"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Main Contact Name *</Label>
                <Input
                  value={formData.main_contact}
                  onChange={(e) => handleInputChange('main_contact', e.target.value)}
                  placeholder="Contact person name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Mobile Phone *</Label>
                <Input
                  value={formData.mobile_phone}
                  onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
                  placeholder="Phone number"
                  className="mt-1"
                />
              </div>
            </div>

            {formData.generated_url && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <Label className="text-green-800 font-medium">Generated Chatbot URL</Label>
                <div className="mt-1 text-green-700 font-mono text-sm break-all">
                  {formData.generated_url}
                </div>
                <p className="text-green-600 text-xs mt-1">
                  This will be the retailer's dedicated chatbot and offers page
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-charcoal mb-2">Business Details & Discounts</h3>
              <p className="text-gray-600">Configure services and discount structure</p>
            </div>

            <div>
              <Label>Type of Services *</Label>
              <Textarea
                value={formData.services_offered}
                onChange={(e) => handleInputChange('services_offered', e.target.value)}
                placeholder="Describe the services offered..."
                rows={3}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Type of Products *</Label>
              <Textarea
                value={formData.products_offered}
                onChange={(e) => handleInputChange('products_offered', e.target.value)}
                placeholder="Describe the products sold..."
                rows={3}
                className="mt-1"
              />
            </div>

            {/* Discount System - Same as retailer onboarding but simplified */}
            <div>
              <Label className="text-lg font-semibold text-charcoal mb-3 block">How would you like to reward shoppers?</Label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <Card 
                  className={`cursor-pointer transition-all duration-200 ${
                    formData.discount_type === 'flat' 
                      ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleInputChange('discount_type', 'flat')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <PoundSterling className="h-5 w-5 text-orange-600" />
                      <div>
                        <h4 className="font-semibold text-charcoal">Flat £ Discount</h4>
                        <p className="text-sm text-gray-600">Same for everyone</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card 
                  className={`cursor-pointer transition-all duration-200 ${
                    formData.discount_type === 'percentage' 
                      ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleInputChange('discount_type', 'percentage')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <Percent className="h-5 w-5 text-green-600" />
                      <div>
                        <h4 className="font-semibold text-charcoal">Category Percentages</h4>
                        <p className="text-sm text-gray-600">Different per group</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Discount Input Fields */}
              {formData.discount_type === 'flat' ? (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <Label className="font-medium">Flat Discount Amount (£)</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-lg font-bold text-gray-700">£</span>
                    <Input
                      type="number"
                      value={formData.flat_discount_amount}
                      onChange={(e) => updateFlatDiscount(e.target.value)}
                      placeholder="e.g. 5.00"
                      min="0"
                      max="100"
                      step="0.01"
                      className="max-w-xs"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <Label className="font-medium mb-3 block">Category Percentage Discounts</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries({
                      students: 'Students',
                      pensioners: 'Pensioners', 
                      government_workers: 'Government Workers',
                      nhs_key_workers: 'NHS/Key Workers',
                      general_public: 'General Public'
                    }).map(([key, label]) => (
                      <div key={key} className="flex items-center gap-3">
                        <Label className="min-w-0 flex-1 font-medium">{label}</Label>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={formData.category_discounts[key]}
                            onChange={(e) => updateCategoryDiscount(key, e.target.value)}
                            placeholder="0"
                            min="0"
                            max="50"
                            className="w-16 text-center"
                          />
                          <span className="text-gray-600">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label>Minimum Expenditure (Optional)</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-lg font-medium text-gray-700">£</span>
                <Input
                  type="number"
                  value={formData.minimum_expenditure}
                  onChange={(e) => handleInputChange('minimum_expenditure', e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="max-w-xs"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-charcoal mb-2">Payment & Review</h3>
              <p className="text-gray-600">Configure payment details and review the listing</p>
            </div>

            {/* Payment Configuration */}
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle>Payment Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="mark-as-paid"
                    checked={formData.mark_as_paid}
                    onCheckedChange={(checked) => handleInputChange('mark_as_paid', checked)}
                  />
                  <Label htmlFor="mark-as-paid" className="font-medium">
                    Mark as Paid (Payment handled manually)
                  </Label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Setup Fee</Label>
                    <div className="flex items-center gap-1">
                      <span>£</span>
                      <Input
                        type="number"
                        value={formData.setup_fee}
                        onChange={(e) => handleInputChange('setup_fee', e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Monthly Fee</Label>
                    <div className="flex items-center gap-1">
                      <span>£</span>
                      <Input
                        type="number"
                        value={formData.monthly_fee}
                        onChange={(e) => handleInputChange('monthly_fee', e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Payment Notes</Label>
                  <Textarea
                    value={formData.payment_notes}
                    onChange={(e) => handleInputChange('payment_notes', e.target.value)}
                    placeholder="e.g., Cash payment received, Invoice #12345, etc."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Listing Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><strong>Business:</strong> {formData.business_name}</div>
                  <div><strong>Package:</strong> {packageDetails[formData.selected_package]?.name}</div>
                  <div><strong>Status:</strong> <Badge className={formData.admin_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>{formData.admin_status}</Badge></div>
                  <div><strong>Payment:</strong> {formData.mark_as_paid ? 'Marked as Paid' : 'Pending'}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 5:
        return (
          <div className="text-center space-y-6">
            <div className="bg-green-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            
            <div>
              <h3 className="text-3xl font-bold text-charcoal mb-4">
                Retailer Successfully Created!
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                The new retailer listing has been created and configured.
              </p>
            </div>

            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <h4 className="font-semibold text-charcoal mb-4">Created Successfully:</h4>
                <div className="text-left space-y-2 text-sm text-gray-700">
                  <div><strong>Business Name:</strong> {confirmation?.businessName}</div>
                  <div><strong>Package:</strong> {confirmation?.package?.name}</div>
                  <div><strong>Status:</strong> {confirmation?.status}</div>
                  <div><strong>Chatbot URL:</strong> <span className="font-mono text-primary">{confirmation?.chatbotUrl}</span></div>
                  <div><strong>Application ID:</strong> {confirmation?.applicationId}</div>
                  <div><strong>Created By:</strong> Admin ({user?.email})</div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Button onClick={onClose} className="w-full bg-primary hover:bg-primary/90">
                Return to Applications
              </Button>
              <Button variant="outline" onClick={() => setCurrentStep(1)} className="w-full">
                Create Another Retailer
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Admin: Create Retailer Listing</h2>
        <Button onClick={onClose} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Applications
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                currentStep >= step.id 
                  ? 'bg-primary border-primary text-white' 
                  : 'border-gray-300 text-gray-400'
              }`}>
                {currentStep > step.id ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  step.icon
                )}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-1 mx-2 transition-colors ${
                  currentStep > step.id ? 'bg-primary' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="text-center">
          <h3 className="font-medium text-charcoal">{steps[currentStep - 1]?.title}</h3>
          <p className="text-sm text-gray-600">Step {currentStep} of {steps.length}</p>
        </div>
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-6">
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      {currentStep < 5 && (
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          <Button
            onClick={nextStep}
            disabled={!validateStep() || isSubmitting}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90"
          >
            {currentStep === 4 ? (
              isSubmitting ? 'Creating...' : 'Create Retailer'
            ) : (
              <>
                Continue
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminRetailerOnboarding;